// DataPage.js (Tree-based, modern UI) — UPDATED (removed DB table section + changed subtitle + modern icon)

import React, { useEffect, useMemo, useState } from "react";
import api from "./api";
import {
  Database,
  ChevronDown,
  ChevronRight,
  Plus,
  Upload,
  Download,
  Pencil,
  Trash2,
  RefreshCw,
  Save,
  X,
  Folder,
  Wrench,
  FileText,
} from "lucide-react";

/**
 * Data hierarchy stored in DataFile table:
 *  - line (string)
 *  - machine (string)
 *  - file entries
 *
 * This UI builds a tree:
 *  Line -> Machine -> Files
 *
 * NOTE:
 * - "Create Line" / "Create Machine" is done by uploading a first file for it.
 * - If you want true "empty lines/machines" saved in DB, we’d add a small table later.
 */

export default function DataPage() {
  // upload form state
  const [line, setLine] = useState("");
  const [machine, setMachine] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);

  // tree data
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // open/close state
  const [openLines, setOpenLines] = useState(() => new Set());
  const [openMachines, setOpenMachines] = useState(() => new Set());

  // inline editing notes
  const [editingId, setEditingId] = useState(null);
  const [editNotesValue, setEditNotesValue] = useState("");

  // “Add machine under line” helper
  const [addingMachineForLine, setAddingMachineForLine] = useState(null);
  const [newMachineName, setNewMachineName] = useState("");

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/data-files");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching files", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to load files: ${msg}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line
  }, []);

  // Build tree: { line -> { machine -> [files...] } }
  const tree = useMemo(() => {
    const byLine = new Map();
    for (const it of items) {
      const L = (it.line || "").trim() || "—";
      const M = (it.machine || "").trim() || "—";
      if (!byLine.has(L)) byLine.set(L, new Map());
      const byMachine = byLine.get(L);
      if (!byMachine.has(M)) byMachine.set(M, []);
      byMachine.get(M).push(it);
    }

    // sort files by newest first
    for (const [, byMachine] of byLine.entries()) {
      for (const [m, arr] of byMachine.entries()) {
        arr.sort((a, b) => (b.id || 0) - (a.id || 0));
        byMachine.set(m, arr);
      }
    }

    // sort lines + machines alphabetically
    const sortedLines = Array.from(byLine.keys()).sort((a, b) =>
      a.localeCompare(b)
    );

    const out = [];
    for (const L of sortedLines) {
      const m = byLine.get(L);
      const machines = Array.from(m.keys()).sort((a, b) => a.localeCompare(b));
      out.push({
        line: L,
        machines: machines.map((name) => ({
          machine: name,
          files: m.get(name) || [],
        })),
      });
    }
    return out;
  }, [items]);

  // helpers
  const toggleLine = (L) => {
    setOpenLines((prev) => {
      const next = new Set(prev);
      next.has(L) ? next.delete(L) : next.add(L);
      return next;
    });
  };

  const toggleMachine = (key) => {
    setOpenMachines((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const resetUploadForm = () => {
    setNotes("");
    setFile(null);
    const input = document.getElementById("datafile-input");
    if (input) input.value = "";
  };

  const upload = async (e) => {
    e.preventDefault();

    if (!line.trim() || !machine.trim() || !file) {
      alert("Please enter Line, Machine, and choose a file.");
      return;
    }

    const form = new FormData();
    form.append("line", line.trim());
    form.append("machine", machine.trim());
    form.append("notes", notes || "");
    form.append("file", file);

    try {
      await api.post("/api/data-files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      resetUploadForm();
      await fetchItems();

      // auto-open the tree location
      setOpenLines((prev) => new Set(prev).add(line.trim()));
      setOpenMachines((prev) => new Set(prev).add(`${line.trim()}::${machine.trim()}`));
    } catch (err) {
      console.error("Upload failed", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Upload failed: ${msg}`);
    }
  };

  const downloadFile = (id) => {
    const base = api.defaults.baseURL?.replace(/\/$/, "") || "";
    window.open(`${base}/api/data-files/${id}/download`, "_blank");
  };

  const deleteFile = async (id) => {
    if (!window.confirm("Delete this file?")) return;

    try {
      await api.delete(`/api/data-files/${id}`);
      await fetchItems();
    } catch (err) {
      console.error("Delete failed", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Delete failed: ${msg}`);
    }
  };

  const startEditNotes = (it) => {
    setEditingId(it.id);
    setEditNotesValue(it.notes || "");
  };

  const cancelEditNotes = () => {
    setEditingId(null);
    setEditNotesValue("");
  };

  const saveNotes = async (id) => {
    try {
      await api.put(`/api/data-files/${id}`, { notes: editNotesValue });
      cancelEditNotes();
      await fetchItems();
    } catch (err) {
      console.error("Edit notes failed", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Update failed: ${msg}`);
    }
  };

  // Add Machine under a Line:
  // We "create" the machine by uploading a file into it (since DB is DataFile only).
  const beginAddMachine = (L) => {
    setAddingMachineForLine(L);
    setNewMachineName("");
  };

  const cancelAddMachine = () => {
    setAddingMachineForLine(null);
    setNewMachineName("");
  };

  const confirmAddMachine = (L) => {
    const m = (newMachineName || "").trim();
    if (!m) return alert("Machine name is required.");
    // Set the upload form to this line + machine and open file picker
    setLine(L);
    setMachine(m);
    cancelAddMachine();

    // optional: focus file input
    setTimeout(() => {
      const input = document.getElementById("datafile-input");
      if (input) input.click();
    }, 50);
  };

  return (
    <div className="main-content">
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          alignItems: "center",
          marginBottom: 18,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, rgba(99,102,241,1) 0%, rgba(139,92,246,1) 100%)",
              color: "white",
              boxShadow: "0 10px 30px rgba(99,102,241,0.25)",
            }}
          >
            <Database size={26} />
          </div>

          <div>
            <h1 style={{ margin: 0, fontSize: 28 }}>
              Data <span>Files</span>
            </h1>
            <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
              Centralized technical documentation structured by production assets.
            </div>
          </div>
        </div>

        <button
          type="button"
          className="button-secondary"
          onClick={fetchItems}
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Upload Card */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(99,102,241,0.12)",
              color: "var(--accent)",
              border: "1px solid rgba(99,102,241,0.18)",
            }}
          >
            <Upload size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Upload a file</div>
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
              Add documentation to a Line + Machine
            </div>
          </div>
        </div>

        <form onSubmit={upload} style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label>Line</label>
              <input
                value={line}
                onChange={(e) => setLine(e.target.value)}
                placeholder="e.g. Line A / Multivac / Production"
              />
            </div>

            <div>
              <label>Machine</label>
              <input
                value={machine}
                onChange={(e) => setMachine(e.target.value)}
                placeholder="e.g. Conveyor / Sealer / Grinder"
              />
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What is this file? revision, procedures, wiring, manual, etc."
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <label>Choose file</label>
            <input
              id="datafile-input"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              marginTop: 14,
            }}
          >
            <button type="submit" className="add-button">
              <Upload size={16} />
              Upload
            </button>

            <button
              type="button"
              className="button-secondary"
              onClick={() => {
                setLine("");
                setMachine("");
                resetUploadForm();
              }}
            >
              <X size={16} />
              Clear
            </button>
          </div>
        </form>
      </div>

      {/* Tree Card */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(16,185,129,0.12)",
                border: "1px solid rgba(16,185,129,0.18)",
                color: "var(--success)",
              }}
            >
              <Folder size={18} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                Library {loading ? <span style={{ color: "var(--text-muted)" }}>(loading...)</span> : null}
              </div>
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
                Expand a line to manage machines and files.
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              className="add-button"
              style={{
                padding: "10px 14px",
                background: "rgba(99,102,241,0.12)",
                border: "1px solid rgba(99,102,241,0.25)",
                color: "var(--accent)",
              }}
              onClick={() => {
                const newLine = window.prompt("Create a new Line name:");
                if (!newLine) return;
                const L = newLine.trim();
                if (!L) return;
                // we open it; it appears only after first upload to that line
                setOpenLines((prev) => new Set(prev).add(L));
                setLine(L);
              }}
              title="Add Line"
            >
              <Plus size={16} /> Line
            </button>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          {tree.length === 0 ? (
            <div style={{ padding: 28, textAlign: "center", color: "var(--text-muted)" }}>
              No files yet. Upload your first file to start building the structure.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {tree.map((L) => {
                const lineKey = L.line;
                const lineOpen = openLines.has(lineKey);

                return (
                  <div
                    key={lineKey}
                    style={{
                      borderRadius: 14,
                      border: "1px solid rgba(148,163,184,0.22)",
                      overflow: "hidden",
                      background: "rgba(255,255,255,0.02)",
                    }}
                  >
                    {/* Line header */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "12px 14px",
                        background: "linear-gradient(135deg, rgba(59,130,246,0.07), rgba(139,92,246,0.06))",
                        borderBottom: lineOpen ? "1px solid rgba(148,163,184,0.18)" : "none",
                      }}
                    >
                      <button
                        type="button"
                        className="icon-btn ghost"
                        onClick={() => toggleLine(lineKey)}
                        title={lineOpen ? "Collapse" : "Expand"}
                        style={{ width: 32, height: 32 }}
                      >
                        {lineOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>

                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(59,130,246,0.12)",
                          border: "1px solid rgba(59,130,246,0.22)",
                          color: "#3b82f6",
                        }}
                      >
                        <Folder size={18} />
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 850, fontSize: 15 }}>{lineKey}</div>
                        <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
                          Machines: {L.machines.length}
                        </div>
                      </div>

                      {lineOpen && (
                        <button
                          type="button"
                          className="add-button"
                          style={{
                            padding: "8px 12px",
                            background: "rgba(139,92,246,0.12)",
                            border: "1px solid rgba(139,92,246,0.25)",
                            color: "#8b5cf6",
                          }}
                          onClick={() => beginAddMachine(lineKey)}
                          title="Add Machine"
                        >
                          <Plus size={16} /> Machine
                        </button>
                      )}
                    </div>

                    {/* Add Machine inline form */}
                    {lineOpen && addingMachineForLine === lineKey && (
                      <div
                        style={{
                          padding: "12px 14px",
                          borderBottom: "1px solid rgba(148,163,184,0.18)",
                          background: "rgba(16,185,129,0.05)",
                        }}
                      >
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 10px",
                              borderRadius: 10,
                              background: "rgba(16,185,129,0.14)",
                              border: "1px solid rgba(16,185,129,0.22)",
                              color: "var(--success)",
                              fontWeight: 800,
                              fontSize: 12,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Plus size={14} /> New Machine
                          </div>

                          <input
                            value={newMachineName}
                            onChange={(e) => setNewMachineName(e.target.value)}
                            placeholder="Machine name..."
                            style={{ flex: 1 }}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") confirmAddMachine(lineKey);
                              if (e.key === "Escape") cancelAddMachine();
                            }}
                          />

                          <button
                            type="button"
                            className="icon-btn success"
                            onClick={() => confirmAddMachine(lineKey)}
                            title="Continue to upload file"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn ghost"
                            onClick={cancelAddMachine}
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        <div style={{ marginTop: 8, color: "var(--text-muted)", fontSize: 12 }}>
                          After creating the machine, we’ll ask you to upload the first file for it.
                        </div>
                      </div>
                    )}

                    {/* Machines */}
                    {lineOpen && (
                      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                        {L.machines.map((M) => {
                          const machineKey = `${lineKey}::${M.machine}`;
                          const machineOpen = openMachines.has(machineKey);

                          return (
                            <div
                              key={machineKey}
                              style={{
                                borderRadius: 12,
                                border: "1px solid rgba(148,163,184,0.22)",
                                overflow: "hidden",
                                background: "rgba(255,255,255,0.02)",
                              }}
                            >
                              {/* Machine header */}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  padding: "10px 12px",
                                  background: "rgba(2,6,23,0.02)",
                                  borderBottom: machineOpen ? "1px solid rgba(148,163,184,0.18)" : "none",
                                }}
                              >
                                <button
                                  type="button"
                                  className="icon-btn ghost"
                                  onClick={() => toggleMachine(machineKey)}
                                  title={machineOpen ? "Collapse" : "Expand"}
                                  style={{ width: 30, height: 30 }}
                                >
                                  {machineOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </button>

                                <div
                                  style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "rgba(139,92,246,0.12)",
                                    border: "1px solid rgba(139,92,246,0.22)",
                                    color: "#8b5cf6",
                                  }}
                                >
                                  <Wrench size={18} />
                                </div>

                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 850, fontSize: 14 }}>{M.machine}</div>
                                  <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
                                    Files: {M.files.length}
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="add-button"
                                  style={{
                                    padding: "8px 12px",
                                    background: "rgba(99,102,241,0.12)",
                                    border: "1px solid rgba(99,102,241,0.25)",
                                    color: "var(--accent)",
                                  }}
                                  onClick={() => {
                                    setLine(lineKey);
                                    setMachine(M.machine);
                                    setTimeout(() => {
                                      const input = document.getElementById("datafile-input");
                                      if (input) input.click();
                                    }, 50);
                                  }}
                                  title="Upload file to this machine"
                                >
                                  <Upload size={16} /> Upload
                                </button>
                              </div>

                              {/* Files */}
                              {machineOpen && (
                                <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                                  {M.files.length === 0 ? (
                                    <div style={{ padding: 14, color: "var(--text-muted)" }}>
                                      No files yet.
                                    </div>
                                  ) : (
                                    M.files.map((it) => {
                                      const isEditing = editingId === it.id;

                                      return (
                                        <div
                                          key={it.id}
                                          style={{
                                            display: "flex",
                                            gap: 10,
                                            alignItems: "center",
                                            padding: "10px 12px",
                                            borderRadius: 12,
                                            border: "1px solid rgba(148,163,184,0.18)",
                                            background: "rgba(255,255,255,0.03)",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: 36,
                                              height: 36,
                                              borderRadius: 12,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              background: "rgba(148,163,184,0.10)",
                                              border: "1px solid rgba(148,163,184,0.18)",
                                              color: "var(--text-muted)",
                                            }}
                                          >
                                            <FileText size={18} />
                                          </div>

                                          <div style={{ flex: 1, minWidth: 260 }}>
                                            <div style={{ fontWeight: 800, fontSize: 14 }}>
                                              {it.original_name || "Unnamed file"}
                                            </div>
                                            <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>
                                              {it.file_type ? `${it.file_type.toUpperCase()} • ` : ""}
                                              {it.uploaded_at ? it.uploaded_at.slice(0, 10) : ""}
                                            </div>

                                            <div style={{ marginTop: 6 }}>
                                              {isEditing ? (
                                                <input
                                                  value={editNotesValue}
                                                  onChange={(e) => setEditNotesValue(e.target.value)}
                                                  placeholder="Notes..."
                                                  style={{ width: "100%" }}
                                                  autoFocus
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Enter") saveNotes(it.id);
                                                    if (e.key === "Escape") cancelEditNotes();
                                                  }}
                                                />
                                              ) : (
                                                <div style={{ color: "var(--text-main)", fontSize: 13 }}>
                                                  <span style={{ color: "var(--text-muted)" }}>Notes: </span>
                                                  {it.notes?.trim() ? it.notes : "—"}
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                            <button
                                              type="button"
                                              className="icon-btn"
                                              onClick={() => downloadFile(it.id)}
                                              title="Download"
                                            >
                                              <Download size={16} />
                                            </button>

                                            {isEditing ? (
                                              <>
                                                <button
                                                  type="button"
                                                  className="icon-btn success"
                                                  onClick={() => saveNotes(it.id)}
                                                  title="Save notes"
                                                >
                                                  <Save size={16} />
                                                </button>
                                                <button
                                                  type="button"
                                                  className="icon-btn ghost"
                                                  onClick={cancelEditNotes}
                                                  title="Cancel"
                                                >
                                                  <X size={16} />
                                                </button>
                                              </>
                                            ) : (
                                              <button
                                                type="button"
                                                className="icon-btn"
                                                onClick={() => startEditNotes(it)}
                                                title="Edit notes"
                                              >
                                                <Pencil size={16} />
                                              </button>
                                            )}

                                            <button
                                              type="button"
                                              className="icon-btn danger"
                                              onClick={() => deleteFile(it.id)}
                                              title="Delete"
                                            >
                                              <Trash2 size={16} />
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Removed the DB table section on purpose (as requested) */}
      </div>
    </div>
  );
}
